/* ============================================================
   TransferLabs — DTF Gang Sheet Builder (multi-design canvas)
   ────────────────────────────────────────────────────────────
   What this is
     A true gang-sheet builder: customer uploads multiple design
     files, we auto-pack them onto a fixed-width sheet, they can
     drag / resize / rotate / duplicate / delete tiles, and we
     auto-pick the smallest length variant whose length fits.

   Architecture
     • Single source of truth = `state` (designs[], tiles[], etc.)
     • All measurements stored in INCHES; converted to pixels
       only at render-time via `state.pxPerIn`.
     • Image loading uses URL.createObjectURL (NOT FileReader/
       base64) so 10–50 large images don't bloat memory.
     • Tiles are absolute-positioned <div>s overlaying the sheet.
       Drag/resize uses Pointer Events.
     • Auto-pack is MaxRects-BSSF (Best Short Side Fit) with 90°
       rotation evaluated per piece for tightest fit.

   Submit / order delivery
     We POST multipart/form-data to /cart/add.js so that each
     uploaded design is attached to the order as its own line
     item property FILE — Shopify auto-stores them on its CDN
     and renders download links on the admin order page next to
     the line item, plus in the customer's confirmation email.
     We also attach the rendered composite PNG ("Sheet preview")
     and a JSON layout (`_Layout`, admin-only) so the production
     team can re-render the sheet at full DPI from originals.

   Coordinate systems
       inches    : truth (xIn, yIn, wIn, hIn)
       px        : display (xIn * pxPerIn, ...)
       export    : higher-fidelity composite render

   Key public events on the section root (for theme integration)
       'tl:gsb:added-to-cart'    detail = { variantId, totalDesigns, totalCopies }
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     CONSTANTS
     ============================================================ */
  const SPACING_PRESETS_IN = [0.125, 0.25, 0.5];
  const SNAP_THRESHOLD_PX = 6;            // distance under which a snap fires
  const GRID_SNAP_IN = 0.25;              // quarter-inch grid
  const MIN_TILE_IN = 0.5;                // hard floor for resize
  const MAX_TILE_IN_DEFAULT_RATIO = 0.95; // initial designs scaled to ≤ 95% of sheet width
  const MIN_PX_PER_IN = 6;                // when sheet is very long, never zoom out below this
  const MAX_PX_PER_IN = 32;               // never zoom in beyond this in canvas
  const EXPORT_PX_PER_IN = 36;            // PNG composite (cart preview file + customer download)
  const FALLBACK_LENGTH_IN = 24;          // empty-canvas placeholder length

  /* ============================================================
     UTILS
     ============================================================ */

  function uid(prefix) {
    return prefix + Math.random().toString(36).slice(2, 9);
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function approxEq(a, b, eps) { return Math.abs(a - b) < (eps == null ? 0.001 : eps); }

  function inchesPretty(inches) {
    const ft = inches / 12;
    if (inches >= 24 && approxEq(ft, Math.round(ft), 0.05)) return Math.round(ft) + ' ft';
    if (inches >= 12) return ft.toFixed(1).replace(/\.0$/, '') + ' ft';
    return inches.toFixed(1).replace(/\.0$/, '') + ' in';
  }

  // Parse "5 Feet" / "30 ft" / "12 inches" / "12 in" → inches.
  function parseLengthIn(label) {
    if (!label) return 0;
    const m = String(label).match(/(\d+(?:\.\d+)?)\s*(ft|feet|foot|in|inch|inches|")/i);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    const u = m[2].toLowerCase();
    if (u.startsWith('ft') || u.startsWith('foot') || u.startsWith('feet')) return n * 12;
    return n;
  }

  // Cents (Shopify) → "$1.23" using money_format if reasonable, else USD.
  function fmtMoney(cents, currency) {
    const n = (cents / 100);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2
      }).format(n);
    } catch (_) {
      return '$' + n.toFixed(2);
    }
  }

  function bytesPretty(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  }

  function downloadDataUrl(dataUrl, filename) {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // Load a File into an HTMLImageElement; resolves with { img, w, h, objectUrl }.
  // We use URL.createObjectURL instead of FileReader + dataUrl so 10–50 large
  // images don't sit base64-inflated in memory. Caller is responsible for
  // calling URL.revokeObjectURL(objectUrl) when the image is no longer needed.
  function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        if (!img.naturalWidth || !img.naturalHeight) {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Could not read image dimensions for ' + file.name));
          return;
        }
        resolve({ img, w: img.naturalWidth, h: img.naturalHeight, objectUrl });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Could not load image: ' + file.name));
      };
      img.src = objectUrl;
    });
  }

  /* ============================================================
     PACK ALGORITHM — MaxRects-BSSF
     Adapted from Jukka Jylänki's "Survey of 2D Packing Algorithms".
     Sheet width is fixed; height grows. We try each rect in two
     orientations (original + 90°) for tighter fits.
     Input rects: [{ w, h, allowRotate, payload }]
     Output: [{ x, y, w, h, rotated, payload }]  — same length
              + { binHeight }
     ============================================================ */

  function packMaxRects(rects, sheetW, spacing) {
    const placed = [];
    let free = [{ x: 0, y: 0, w: sheetW, h: Infinity }];

    // Sort by max side descending — gives near-optimal packing in practice.
    const order = rects
      .map((r, i) => ({ i, w: r.w, h: r.h, allowRotate: r.allowRotate, payload: r.payload }))
      .sort((a, b) => Math.max(b.w, b.h) - Math.max(a.w, a.h));

    function bssfScore(freeRect, w, h) {
      // Best Short Side Fit: minimize the smallest leftover dimension.
      const leftoverX = freeRect.w - w;
      const leftoverY = freeRect.h - h;
      const shortSide = Math.min(leftoverX, leftoverY);
      const longSide = Math.max(leftoverX, leftoverY);
      return { primary: shortSide, secondary: longSide };
    }

    function findBestPlacement(reqW, reqH) {
      let best = null;
      for (let i = 0; i < free.length; i++) {
        const f = free[i];
        if (f.w < reqW - 1e-6 || f.h < reqH - 1e-6) continue;
        const sc = bssfScore(f, reqW, reqH);
        // Tie-break by topmost (y), then leftmost (x) to keep things tidy.
        if (!best
          || sc.primary < best.score.primary - 1e-6
          || (approxEq(sc.primary, best.score.primary) && sc.secondary < best.score.secondary - 1e-6)
          || (approxEq(sc.primary, best.score.primary) && approxEq(sc.secondary, best.score.secondary) && f.y < best.f.y - 1e-6)
          || (approxEq(sc.primary, best.score.primary) && approxEq(sc.secondary, best.score.secondary) && approxEq(f.y, best.f.y) && f.x < best.f.x - 1e-6)
        ) {
          best = { f, score: sc, x: f.x, y: f.y };
        }
      }
      return best;
    }

    function splitFreeRect(freeRect, used) {
      // Returns 0–4 new free rects after placing `used` inside `freeRect`.
      const newRects = [];
      // No overlap → keep as-is
      if (used.x >= freeRect.x + freeRect.w - 1e-6 || used.x + used.w <= freeRect.x + 1e-6
        || used.y >= freeRect.y + freeRect.h - 1e-6 || used.y + used.h <= freeRect.y + 1e-6) {
        return [freeRect];
      }
      // Split top
      if (used.y > freeRect.y + 1e-6) {
        newRects.push({ x: freeRect.x, y: freeRect.y, w: freeRect.w, h: used.y - freeRect.y });
      }
      // Split bottom
      if (used.y + used.h < freeRect.y + freeRect.h - 1e-6) {
        newRects.push({
          x: freeRect.x,
          y: used.y + used.h,
          w: freeRect.w,
          h: freeRect.h - (used.y + used.h - freeRect.y)
        });
      }
      // Split left
      if (used.x > freeRect.x + 1e-6) {
        newRects.push({ x: freeRect.x, y: freeRect.y, w: used.x - freeRect.x, h: freeRect.h });
      }
      // Split right
      if (used.x + used.w < freeRect.x + freeRect.w - 1e-6) {
        newRects.push({
          x: used.x + used.w,
          y: freeRect.y,
          w: freeRect.w - (used.x + used.w - freeRect.x),
          h: freeRect.h
        });
      }
      return newRects;
    }

    function pruneFree() {
      // Remove any rect contained inside another.
      for (let i = 0; i < free.length; i++) {
        for (let j = i + 1; j < free.length; j++) {
          const a = free[i], b = free[j];
          if (a.x >= b.x - 1e-6 && a.y >= b.y - 1e-6
            && a.x + a.w <= b.x + b.w + 1e-6 && a.y + a.h <= b.y + b.h + 1e-6) {
            free.splice(i, 1); i--; break;
          }
          if (b.x >= a.x - 1e-6 && b.y >= a.y - 1e-6
            && b.x + b.w <= a.x + a.w + 1e-6 && b.y + b.h <= a.y + a.h + 1e-6) {
            free.splice(j, 1); j--;
          }
        }
      }
    }

    let maxY = 0;
    const result = new Array(rects.length);

    for (let k = 0; k < order.length; k++) {
      const r = order[k];
      const padW = r.w + spacing;
      const padH = r.h + spacing;
      const padWR = r.h + spacing;
      const padHR = r.w + spacing;

      // Try unrotated and rotated, pick best score.
      const candNo = findBestPlacement(padW, padH);
      const candRot = r.allowRotate ? findBestPlacement(padWR, padHR) : null;

      let chosen = null;
      let chosenW, chosenH, rotated;

      if (candNo && candRot) {
        // Pick the better-scoring placement.
        if (candRot.score.primary < candNo.score.primary - 1e-6
          || (approxEq(candRot.score.primary, candNo.score.primary) && candRot.score.secondary < candNo.score.secondary - 1e-6)) {
          chosen = candRot; chosenW = padWR; chosenH = padHR; rotated = true;
        } else {
          chosen = candNo; chosenW = padW; chosenH = padH; rotated = false;
        }
      } else if (candNo) {
        chosen = candNo; chosenW = padW; chosenH = padH; rotated = false;
      } else if (candRot) {
        chosen = candRot; chosenW = padWR; chosenH = padHR; rotated = true;
      } else {
        // Should never happen with infinite-height bin, unless rect.w > sheetW.
        // Fall back to placing at (0, currentMaxY) with whichever fits widthwise.
        const fitsNo = r.w <= sheetW + 1e-6;
        const fitsRo = r.allowRotate && r.h <= sheetW + 1e-6;
        const fy = maxY;
        if (fitsRo && (!fitsNo || r.h < r.w)) {
          chosen = { x: 0, y: fy }; chosenW = padWR; chosenH = padHR; rotated = true;
        } else {
          chosen = { x: 0, y: fy }; chosenW = padW; chosenH = padH; rotated = false;
        }
      }

      const placedRect = {
        x: chosen.x,
        y: chosen.y,
        w: chosenW - spacing,
        h: chosenH - spacing,
        rotated,
        payload: r.payload
      };
      result[r.i] = placedRect;
      maxY = Math.max(maxY, chosen.y + chosenH);

      // Update free list: split everything intersected by this used+padding rect.
      const used = { x: chosen.x, y: chosen.y, w: chosenW, h: chosenH };
      const newFree = [];
      for (let i = 0; i < free.length; i++) {
        const splitParts = splitFreeRect(free[i], used);
        for (let j = 0; j < splitParts.length; j++) newFree.push(splitParts[j]);
      }
      free = newFree;
      pruneFree();
    }

    return { tiles: result, binHeight: maxY };
  }

  /* ============================================================
     INIT — one builder per section root
     ============================================================ */

  function initBuilder(root) {
    if (!root || root.dataset.tlGsbReady === '1') return;
    root.dataset.tlGsbReady = '1';

    /* ---- Config ---- */
    const cfg = {
      sheetWidthIn:    parseFloat(root.dataset.sheetWidth) || 16,
      dpi:             parseFloat(root.dataset.dpi) || 300,
      maxMb:           parseFloat(root.dataset.maxMb) || 50,
      maxDesigns:      parseInt(root.dataset.maxDesigns, 10) || 50,
      defaultSpacing:  parseFloat(root.dataset.defaultSpacing) || 0.25,
      minDesignIn:    parseFloat(root.dataset.minDesignIn) || 0.5,
      cartAddUrl:      root.dataset.cartAddUrl || '/cart/add.js',
      cartUrl:         root.dataset.cartUrl || '/cart',
      currency:        root.dataset.currency || 'USD',
      productHandle:   root.dataset.productHandle || '',
    };

    /* ---- DOM refs ---- */
    const dom = {
      frame:        root.querySelector('[data-tl-gsb-frame]'),
      viewport:     root.querySelector('[data-tl-gsb-viewport]'),
      sheet:        root.querySelector('[data-tl-gsb-sheet]'),
      emptyMsg:     root.querySelector('[data-tl-gsb-empty-msg]'),
      rulerX:       root.querySelector('[data-tl-gsb-ruler-x]'),
      rulerY:       root.querySelector('[data-tl-gsb-ruler-y]'),
      sheetW:       root.querySelector('[data-tl-gsb-sheet-w]'),
      sheetH:       root.querySelector('[data-tl-gsb-sheet-h]'),

      fileInput:    root.querySelector('[data-tl-gsb-file]'),
      drop:         root.querySelector('[data-tl-gsb-drop]'),
      uploadErr:    root.querySelector('[data-tl-gsb-upload-err]'),
      designsList:  root.querySelector('[data-tl-gsb-designs]'),
      designCount:  root.querySelector('[data-tl-gsb-design-count]'),

      sizes:        root.querySelector('[data-tl-gsb-sizes]'),
      recommend:    root.querySelector('[data-tl-gsb-recommend]'),
      recommendName:root.querySelector('[data-tl-gsb-recommend-name]'),

      bestPpi:      root.querySelector('[data-tl-gsb-best-ppi]'),

      sumLabel:     root.querySelector('[data-tl-gsb-sum-label]'),
      sumLine:      root.querySelector('[data-tl-gsb-sum-line]'),
      saveRow:      root.querySelector('[data-tl-gsb-save-row]'),
      save:         root.querySelector('[data-tl-gsb-save]'),
      total:        root.querySelector('[data-tl-gsb-total]'),

      atc:          root.querySelector('[data-tl-gsb-submit]'),
      atcLabel:     root.querySelector('[data-tl-gsb-atc-label]'),
      err:          root.querySelector('[data-tl-gsb-error]'),

      autoArrange:  root.querySelector('[data-tl-gsb-autoarrange]'),
      download:     root.querySelector('[data-tl-gsb-download]'),
      spacingGroup: root.querySelector('[data-tl-gsb-spacing]'),

      fillArea:     root.querySelector('[data-tl-gsb-fill-area]'),
      fillTotal:    root.querySelector('[data-tl-gsb-fill-total]'),
      fillPct:      root.querySelector('[data-tl-gsb-fill-pct]'),
      fillBar:      root.querySelector('[data-tl-gsb-fill-bar]'),
    };

    /* ---- Variants ---- */
    let variants = [];
    try {
      const raw = root.querySelector('[data-tl-gsb-variants]');
      const arr = raw ? JSON.parse(raw.textContent) : [];
      variants = arr
        .map(v => ({
          id: v.id,
          title: v.title,
          option1: v.option1,
          price: v.price,
          compareAt: v.compare_at_price,
          available: v.available,
          lengthIn: parseLengthIn(v.option1) || parseLengthIn(v.title),
        }))
        .filter(v => v.lengthIn > 0)
        .sort((a, b) => a.lengthIn - b.lengthIn);
    } catch (e) {
      console.error('[tl-gsb] could not parse variants', e);
    }

    /* ---- State ---- */
    const state = {
      designs: [],            // { id, name, file, objectUrl, img, naturalW, naturalH, wIn, hIn, qty, rotation }
      tiles:   [],            // { id, designId, copyIndex, xIn, yIn, wIn, hIn }
      selectedTileIds: new Set(),
      spacingIn: cfg.defaultSpacing,
      pxPerIn: 24,
      sheetLengthIn: FALLBACK_LENGTH_IN,  // visual canvas length (matches selected variant or recommended)
      packedHeightIn: 0,
      variantId: null,
      variantOverridden: false,
      isPacking: false,
      isDownloading: false,
      isSubmitting: false,
    };

    if (variants[0]) {
      state.variantId = variants[0].id;
      state.sheetLengthIn = variants[0].lengthIn;
    }

    /* ============================================================
       LAYOUT — pack + length recommendation + scale + render
       ============================================================ */

    function buildTilesFromDesigns() {
      // Generate one tile per (design × copy). Keep existing positions
      // for matching tile IDs so manual drags survive qty changes.
      const oldByKey = new Map();
      state.tiles.forEach(t => oldByKey.set(t.designId + ':' + t.copyIndex, t));

      const next = [];
      state.designs.forEach(d => {
        for (let c = 0; c < d.qty; c++) {
          const key = d.id + ':' + c;
          const old = oldByKey.get(key);
          const dispW = d.rotation === 90 ? d.hIn : d.wIn;
          const dispH = d.rotation === 90 ? d.wIn : d.hIn;
          if (old) {
            // Keep position; refresh dims (in case the design was resized in the list)
            old.wIn = dispW;
            old.hIn = dispH;
            next.push(old);
          } else {
            next.push({
              id: uid('t-'),
              designId: d.id,
              copyIndex: c,
              xIn: 0, yIn: 0,
              wIn: dispW, hIn: dispH,
              isPlaced: false,
            });
          }
        }
      });
      state.tiles = next;
    }

    function autoPack() {
      buildTilesFromDesigns();
      if (!state.tiles.length) {
        state.packedHeightIn = 0;
        return;
      }
      // For pack input we use intrinsic w/h with allowRotate based on
      // whether the design has rotation === 0 (allow free per-tile rotation
      // in pack). When the user has manually rotated a design (rotation === 90),
      // we lock orientation to match — otherwise their rotate tap is useless.
      const inputs = state.tiles.map(t => {
        const d = state.designs.find(dd => dd.id === t.designId);
        const intrinsicW = d ? d.wIn : t.wIn;
        const intrinsicH = d ? d.hIn : t.hIn;
        if (d && d.rotation === 90) {
          // Locked rotated
          return { w: intrinsicH, h: intrinsicW, allowRotate: false, payload: t };
        }
        return { w: intrinsicW, h: intrinsicH, allowRotate: true, payload: t };
      });

      const result = packMaxRects(inputs, cfg.sheetWidthIn, state.spacingIn);
      result.tiles.forEach(p => {
        const t = p.payload;
        t.xIn = p.x;
        t.yIn = p.y;
        t.wIn = p.w;
        t.hIn = p.h;
        t.isPlaced = true;
      });
      state.packedHeightIn = Math.max(0, result.binHeight - state.spacingIn);
    }

    function recommendVariant() {
      if (!variants.length) return null;
      // Smallest variant whose length ≥ packedHeight + a tiny buffer.
      const buf = 0.25; // give 0.25" of safety on the bottom edge
      const target = state.packedHeightIn + buf;
      const fit = variants.find(v => v.lengthIn >= target && v.available !== false);
      return fit || variants[variants.length - 1];
    }

    function applyVariantSelection() {
      // Auto-pick (or keep manually-overridden) variant whose length ≥ packed.
      const rec = recommendVariant();
      if (!rec) return;
      if (state.variantOverridden) {
        // If the override is too small, force-clear the override.
        const v = variants.find(vv => vv.id === state.variantId);
        if (v && v.lengthIn < state.packedHeightIn + 0.05) {
          state.variantOverridden = false;
          state.variantId = rec.id;
        }
      } else {
        state.variantId = rec.id;
      }
      const v = variants.find(vv => vv.id === state.variantId);
      state.sheetLengthIn = v ? v.lengthIn : rec.lengthIn;
    }

    function updateScale() {
      // Compute pxPerIn so the sheet fits the viewport horizontally,
      // capped to MAX_PX_PER_IN, and never below MIN_PX_PER_IN even
      // if the viewport is tiny (we'd rather scroll horizontally).
      const vp = dom.viewport;
      const pad = 24; // breathing room around sheet
      const avail = Math.max(120, vp.clientWidth - pad);
      const pxPerIn = clamp(avail / cfg.sheetWidthIn, MIN_PX_PER_IN, MAX_PX_PER_IN);
      state.pxPerIn = pxPerIn;
      root.style.setProperty('--tl-gsb-px-per-in', pxPerIn);
      root.style.setProperty('--tl-gsb-sheet-w-in', cfg.sheetWidthIn);
      root.style.setProperty('--tl-gsb-sheet-h-in', state.sheetLengthIn);
    }

    /* ============================================================
       RENDER — designs list, sheet+tiles, rulers, chips, summary
       ============================================================ */

    function paintDesignsList() {
      const list = dom.designsList;
      // Use a fresh fragment to keep render simple. (Total rows ≤ 50.)
      const frag = document.createDocumentFragment();

      state.designs.forEach((d) => {
        const row = document.createElement('div');
        row.className = 'tl-gsb__design-row';
        row.setAttribute('role', 'listitem');
        row.dataset.designId = d.id;

        const thumb = document.createElement('div');
        thumb.className = 'tl-gsb__design-thumb';
        const tImg = document.createElement('img');
        tImg.src = d.objectUrl;
        tImg.alt = '';
        thumb.appendChild(tImg);

        const info = document.createElement('div');
        info.className = 'tl-gsb__design-info';
        const name = document.createElement('span');
        name.className = 'tl-gsb__design-name';
        name.textContent = d.name;
        const dims = document.createElement('span');
        dims.className = 'tl-gsb__design-dims';
        dims.textContent = `${d.wIn.toFixed(1)}″ × ${d.hIn.toFixed(1)}″${d.rotation === 90 ? ' · rotated' : ''}`;
        info.appendChild(name);
        info.appendChild(dims);

        const actions = document.createElement('div');
        actions.className = 'tl-gsb__design-actions';

        // qty stepper
        const qty = document.createElement('div');
        qty.className = 'tl-gsb__qty';
        qty.title = 'Copies of this design';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.textContent = '−';
        minus.setAttribute('aria-label', 'Decrease quantity');
        const qInput = document.createElement('input');
        qInput.type = 'number';
        qInput.inputMode = 'numeric';
        qInput.min = '1';
        qInput.max = String(cfg.maxDesigns);
        qInput.value = String(d.qty);
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.textContent = '+';
        plus.setAttribute('aria-label', 'Increase quantity');

        minus.addEventListener('click', () => setDesignQty(d.id, d.qty - 1));
        plus.addEventListener('click', () => setDesignQty(d.id, d.qty + 1));
        qInput.addEventListener('change', () => {
          const v = parseInt(qInput.value, 10);
          setDesignQty(d.id, isFinite(v) ? v : 1);
        });

        qty.appendChild(minus);
        qty.appendChild(qInput);
        qty.appendChild(plus);

        // rotate
        const rot = document.createElement('button');
        rot.type = 'button';
        rot.className = 'tl-gsb__icon-btn';
        rot.title = 'Rotate 90°';
        rot.setAttribute('aria-label', 'Rotate design 90°');
        rot.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>';
        rot.addEventListener('click', () => rotateDesign(d.id));

        // delete
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'tl-gsb__icon-btn tl-gsb__icon-btn--danger';
        del.title = 'Remove design';
        del.setAttribute('aria-label', 'Remove design');
        del.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>';
        del.addEventListener('click', () => removeDesign(d.id));

        actions.appendChild(qty);
        actions.appendChild(rot);
        actions.appendChild(del);

        row.appendChild(thumb);
        row.appendChild(info);
        row.appendChild(actions);
        frag.appendChild(row);
      });

      list.replaceChildren(frag);
      dom.designCount.textContent = state.designs.length === 1
        ? '1 design'
        : state.designs.length + ' designs';
    }

    function paintSheetAndTiles() {
      const px = state.pxPerIn;
      const sheetW = cfg.sheetWidthIn;
      const sheetH = state.sheetLengthIn;

      const sheetWPx = Math.round(sheetW * px);
      const sheetHPx = Math.round(sheetH * px);
      dom.sheet.style.width  = sheetWPx + 'px';
      dom.sheet.style.height = sheetHPx + 'px';

      const empty = state.tiles.length === 0;
      dom.sheet.classList.toggle('tl-gsb__sheet--empty', empty);
      if (dom.emptyMsg) dom.emptyMsg.style.display = empty ? '' : 'none';

      // Re-render tiles (small N, simple replaceChildren is fine)
      const tilesFrag = document.createDocumentFragment();
      state.tiles.forEach(t => {
        const d = state.designs.find(dd => dd.id === t.designId);
        if (!d) return;
        const tileEl = document.createElement('div');
        tileEl.className = 'tl-gsb__tile';
        tileEl.dataset.tileId = t.id;
        tileEl.style.left   = (t.xIn * px) + 'px';
        tileEl.style.top    = (t.yIn * px) + 'px';
        tileEl.style.width  = (t.wIn * px) + 'px';
        tileEl.style.height = (t.hIn * px) + 'px';
        if (state.selectedTileIds.has(t.id)) tileEl.classList.add('is-selected');

        // Image rendering — when rotation is 90° we wrap the <img> in an inner
        // box that's the UN-rotated dims and rotate the wrapper. After rotation
        // the wrapper visually occupies the swapped dims, so it fills the tile
        // exactly while object-fit: contain still works for the image inside.
        if (d.rotation === 90) {
          tileEl.style.overflow = 'hidden';
          const wrap = document.createElement('div');
          // Tile is (t.wIn × t.hIn). Un-rotated dims are (t.hIn × t.wIn).
          wrap.style.cssText =
            'position:absolute;left:50%;top:50%;' +
            'width:' + (t.hIn * px) + 'px;' +
            'height:' + (t.wIn * px) + 'px;' +
            'transform:translate(-50%,-50%) rotate(90deg);' +
            'transform-origin:center center;';
          const img = document.createElement('img');
          img.className = 'tl-gsb__tile-img';
          img.src = d.objectUrl;
          img.alt = '';
          img.draggable = false;
          wrap.appendChild(img);
          tileEl.appendChild(wrap);
        } else {
          const img = document.createElement('img');
          img.className = 'tl-gsb__tile-img';
          img.src = d.objectUrl;
          img.alt = '';
          img.draggable = false;
          tileEl.appendChild(img);
        }

        // Selection handles
        ['nw','ne','sw','se'].forEach(corner => {
          const h = document.createElement('span');
          h.className = 'tl-gsb__handle tl-gsb__handle--' + corner;
          h.dataset.handle = corner;
          tileEl.appendChild(h);
        });

        // Floating actions
        const actions = document.createElement('div');
        actions.className = 'tl-gsb__tile-actions';
        actions.innerHTML = `
          <button type="button" class="tl-gsb__tile-action" data-tile-action="rotate" title="Rotate this design 90°" aria-label="Rotate design">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          </button>
          <button type="button" class="tl-gsb__tile-action" data-tile-action="duplicate" title="Duplicate this design" aria-label="Duplicate">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
          <button type="button" class="tl-gsb__tile-action tl-gsb__tile-action--danger" data-tile-action="delete" title="Remove" aria-label="Remove">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
          </button>
        `;
        tileEl.appendChild(actions);

        // Live size readout
        const sz = document.createElement('span');
        sz.className = 'tl-gsb__tile-size';
        sz.textContent = `${t.wIn.toFixed(2)}″ × ${t.hIn.toFixed(2)}″`;
        tileEl.appendChild(sz);

        attachTilePointerEvents(tileEl, t);

        tilesFrag.appendChild(tileEl);
      });

      // Length marker (shows where the customer's chosen length stops, useful when
      // they intentionally pick a longer sheet than packing requires).
      if (state.tiles.length && state.packedHeightIn > 0 && state.sheetLengthIn > state.packedHeightIn + 0.5) {
        const marker = document.createElement('div');
        marker.className = 'tl-gsb__length-marker';
        marker.style.top = (state.packedHeightIn * px) + 'px';
        marker.dataset.label = 'designs end · ' + inchesPretty(state.packedHeightIn);
        tilesFrag.appendChild(marker);
      }

      // Replace all sheet children except empty message
      Array.from(dom.sheet.children).forEach(c => {
        if (c !== dom.emptyMsg) c.remove();
      });
      dom.sheet.appendChild(tilesFrag);
    }

    function paintRulers() {
      const px = state.pxPerIn;
      // X ruler
      const xLen = Math.round(cfg.sheetWidthIn * px);
      const margin = (dom.viewport.clientWidth - xLen) / 2;
      const xMargin = Math.max(12, margin);
      let xHtml = '';
      for (let i = 0; i <= cfg.sheetWidthIn; i++) {
        const left = (xMargin + i * px);
        const major = (i % 4 === 0);
        xHtml += `<div class="tl-gsb__ruler-tick ${major ? 'tl-gsb__ruler-tick--major' : ''}" style="left:${left}px;top:${major ? 0 : 8}px;width:1px;height:${major ? 14 : 6}px;"></div>`;
        if (major) xHtml += `<div class="tl-gsb__ruler-tick-label" style="left:${left + 3}px;top:2px;">${i}</div>`;
      }
      dom.rulerX.innerHTML = xHtml;

      // Y ruler — relative to viewport scrollTop, but for simplicity we render full length.
      const yLen = Math.round(state.sheetLengthIn * px);
      let yHtml = '';
      const yStart = 12; // matches the sheet's margin: 12px auto
      for (let i = 0; i <= state.sheetLengthIn; i++) {
        const top = (yStart + i * px);
        const major = (i % 6 === 0);
        yHtml += `<div class="tl-gsb__ruler-tick ${major ? 'tl-gsb__ruler-tick--major' : ''}" style="top:${top}px;left:${major ? 0 : 12}px;width:${major ? 22 : 8}px;height:1px;"></div>`;
        if (major) yHtml += `<div class="tl-gsb__ruler-tick-label" style="top:${top + 2}px;left:2px;">${i}</div>`;
      }
      dom.rulerY.innerHTML = yHtml;
    }

    function paintLengthChips() {
      if (!variants.length) return;
      const hasTiles = state.tiles.length > 0;
      const rec = hasTiles ? recommendVariant() : null;
      const recId = rec ? rec.id : null;
      const frag = document.createDocumentFragment();
      variants.forEach(v => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tl-gsb__size';
        btn.dataset.variantId = v.id;
        btn.setAttribute('role', 'radio');
        const tooSmall = state.packedHeightIn > 0 && v.lengthIn < state.packedHeightIn - 0.05;
        if (tooSmall) btn.classList.add('is-toosmall');
        if (v.id === state.variantId) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');
        } else {
          btn.setAttribute('aria-checked', 'false');
        }
        if (v.id === recId && !tooSmall) btn.classList.add('is-recommended');

        const off = document.createElement('span');
        off.className = 'tl-gsb__size-off';
        const compare = v.compareAt || 0;
        const pct = (compare > v.price) ? Math.round(((compare - v.price) / compare) * 100) : 0;
        if (v.id === recId && !tooSmall) {
          off.textContent = '★ Best Fit';
        } else if (pct > 0) {
          off.textContent = pct + '% OFF';
        } else {
          off.textContent = 'Base price';
        }

        const name = document.createElement('span');
        name.className = 'tl-gsb__size-name';
        name.textContent = v.option1 || v.title;

        const price = document.createElement('span');
        price.className = 'tl-gsb__size-price';
        price.textContent = fmtMoney(v.price, cfg.currency);

        btn.appendChild(off);
        btn.appendChild(name);
        btn.appendChild(price);

        if (compare > v.price) {
          const was = document.createElement('span');
          was.className = 'tl-gsb__size-was';
          was.textContent = fmtMoney(compare, cfg.currency);
          btn.appendChild(was);
        }

        btn.disabled = (v.available === false);
        btn.addEventListener('click', () => {
          if (tooSmall || btn.disabled) return;
          state.variantOverridden = (v.id !== (recommendVariant() && recommendVariant().id));
          state.variantId = v.id;
          state.sheetLengthIn = v.lengthIn;
          paint();
        });

        frag.appendChild(btn);
      });
      dom.sizes.replaceChildren(frag);

      if (rec && hasTiles) {
        dom.recommend.hidden = false;
        dom.recommendName.textContent = rec.option1 || rec.title;
      } else {
        dom.recommend.hidden = true;
      }

      // Best ppi shown in the header
      if (variants.length) {
        const sortedByPpi = variants
          .map(v => ({
            v,
            ppi: v.price / 100 / (cfg.sheetWidthIn * v.lengthIn),
          }))
          .sort((a, b) => a.ppi - b.ppi);
        const best = sortedByPpi[0];
        if (best && dom.bestPpi) {
          dom.bestPpi.textContent = '$' + best.ppi.toFixed(3).replace(/0$/, '');
        }
      }
    }

    function paintFillMeter() {
      const sheetArea = cfg.sheetWidthIn * state.sheetLengthIn;
      let designArea = 0;
      state.tiles.forEach(t => { designArea += t.wIn * t.hIn; });
      const pct = sheetArea > 0 ? Math.min(100, Math.round((designArea / sheetArea) * 100)) : 0;
      dom.fillArea.textContent = Math.round(designArea);
      dom.fillTotal.textContent = Math.round(sheetArea);
      dom.fillPct.textContent = pct + '%';
      dom.fillBar.style.width = pct + '%';
    }

    function paintSummary() {
      const v = variants.find(vv => vv.id === state.variantId);
      const hasDesigns = state.designs.length > 0 && state.tiles.length > 0;

      if (!v) {
        dom.sumLabel.textContent = 'Add designs to see pricing';
        dom.sumLine.textContent = '—';
        dom.total.textContent = '—';
        dom.saveRow.hidden = true;
        dom.atc.disabled = true;
        dom.atcLabel.textContent = 'Add at least one design';
        return;
      }

      dom.sumLabel.textContent = `${v.option1 || v.title} sheet × 1`;
      dom.sumLine.textContent = fmtMoney(v.price, cfg.currency);
      dom.total.textContent = fmtMoney(v.price, cfg.currency);

      if (v.compareAt && v.compareAt > v.price) {
        dom.save.textContent = fmtMoney(v.compareAt - v.price, cfg.currency);
        dom.saveRow.hidden = false;
      } else {
        dom.saveRow.hidden = true;
      }

      const fits = state.packedHeightIn <= v.lengthIn + 0.05;
      if (!hasDesigns) {
        dom.atc.disabled = true;
        dom.atcLabel.textContent = 'Add at least one design';
      } else if (!fits) {
        dom.atc.disabled = true;
        dom.atcLabel.textContent = 'Designs don\'t fit on this sheet';
      } else {
        dom.atc.disabled = false;
        dom.atcLabel.textContent = `Add to Cart — ${fmtMoney(v.price, cfg.currency)}`;
      }

      dom.sheetW.textContent = cfg.sheetWidthIn;
      dom.sheetH.textContent = inchesPretty(v.lengthIn);

      dom.download.disabled = !hasDesigns;
    }

    function paint() {
      autoPack();
      applyVariantSelection();
      updateScale();
      paintRulers();
      paintSheetAndTiles();
      paintDesignsList();
      paintLengthChips();
      paintFillMeter();
      paintSummary();
    }

    /* ============================================================
       UPLOAD HANDLING
       ============================================================ */

    async function addFiles(fileList) {
      dom.uploadErr.hidden = true;
      const files = Array.from(fileList || []);
      if (!files.length) return;

      if (state.designs.length + files.length > cfg.maxDesigns) {
        const allowed = Math.max(0, cfg.maxDesigns - state.designs.length);
        if (allowed === 0) {
          showUploadError(`You've already reached the ${cfg.maxDesigns}-design max for one sheet. Remove a design to add another.`);
          return;
        }
        showUploadError(`Adding only the first ${allowed} files — you can have at most ${cfg.maxDesigns} designs per sheet.`);
        files.length = allowed;
      }

      const errors = [];
      for (const file of files) {
        try {
          await addOneFile(file);
        } catch (e) {
          errors.push(file.name + ': ' + e.message);
        }
      }
      if (errors.length) {
        showUploadError(errors.join('\n'));
      }
      paint();
    }

    async function addOneFile(file) {
      // size check
      if (file.size > cfg.maxMb * 1024 * 1024) {
        throw new Error('exceeds ' + cfg.maxMb + 'MB max (file is ' + bytesPretty(file.size) + ')');
      }
      // type check (rough)
      const isImg = /^image\//.test(file.type) || /\.(png|jpe?g|svg|webp|gif)$/i.test(file.name);
      if (!isImg) throw new Error('not a supported image format');

      const { img, w, h, objectUrl } = await loadImageFromFile(file);

      // Initial inches: pixels / DPI, but cap to 95% of sheet width to keep things sensible.
      let wIn = w / cfg.dpi;
      let hIn = h / cfg.dpi;
      // SVGs sometimes report tiny natural dimensions; nudge to a reasonable default.
      if (wIn < 1 && hIn < 1) {
        const targetIn = Math.min(6, cfg.sheetWidthIn * 0.6);
        const scale = targetIn / Math.max(wIn, hIn);
        wIn *= scale;
        hIn *= scale;
      }
      // Clamp: don't let any single design exceed the sheet width minus tiny safety.
      const maxW = cfg.sheetWidthIn * MAX_TILE_IN_DEFAULT_RATIO;
      if (wIn > maxW) {
        const sc = maxW / wIn;
        wIn = maxW;
        hIn = hIn * sc;
      }
      // Floor
      wIn = Math.max(cfg.minDesignIn, wIn);
      hIn = Math.max(cfg.minDesignIn, hIn);

      const design = {
        id: uid('d-'),
        name: file.name,
        size: file.size,
        type: file.type,
        // Keep the original File blob — it gets re-uploaded as a line-item-property
        // file when the customer adds the sheet to cart, which makes it appear as a
        // download link on the order in Shopify admin.
        file: file,
        objectUrl: objectUrl,
        img: img,
        naturalW: w,
        naturalH: h,
        wIn: wIn,
        hIn: hIn,
        qty: 1,
        rotation: 0,
      };
      state.designs.push(design);
    }

    function showUploadError(msg) {
      if (!dom.uploadErr) return;
      dom.uploadErr.textContent = msg;
      dom.uploadErr.hidden = false;
    }

    /* ============================================================
       DESIGN MUTATIONS
       ============================================================ */

    function setDesignQty(designId, newQty) {
      const d = state.designs.find(dd => dd.id === designId);
      if (!d) return;
      const total = state.designs.reduce((s, dd) => s + (dd.id === designId ? 0 : dd.qty), 0);
      const max = cfg.maxDesigns;
      const cap = Math.max(1, max - total);
      d.qty = clamp(parseInt(newQty, 10) || 1, 1, cap);
      paint();
    }

    function rotateDesign(designId) {
      const d = state.designs.find(dd => dd.id === designId);
      if (!d) return;
      d.rotation = (d.rotation === 0) ? 90 : 0;
      // After rotating, displayed width = the OTHER intrinsic dimension.
      // If that overflows the sheet width, scale the design proportionally.
      const displayedW = d.rotation === 90 ? d.hIn : d.wIn;
      if (displayedW > cfg.sheetWidthIn * 0.99) {
        const sc = (cfg.sheetWidthIn * MAX_TILE_IN_DEFAULT_RATIO) / displayedW;
        d.wIn = Math.max(cfg.minDesignIn, d.wIn * sc);
        d.hIn = Math.max(cfg.minDesignIn, d.hIn * sc);
      }
      paint();
    }

    function removeDesign(designId) {
      const target = state.designs.find(dd => dd.id === designId);
      if (target && target.objectUrl) {
        try { URL.revokeObjectURL(target.objectUrl); } catch (_) { /* ok */ }
      }
      state.designs = state.designs.filter(dd => dd.id !== designId);
      // Also clear any selected tiles tied to that design.
      state.tiles = state.tiles.filter(t => t.designId !== designId);
      state.selectedTileIds.forEach(id => {
        if (!state.tiles.find(t => t.id === id)) state.selectedTileIds.delete(id);
      });
      paint();
    }

    function revokeAllDesignUrls() {
      state.designs.forEach(d => {
        if (d.objectUrl) {
          try { URL.revokeObjectURL(d.objectUrl); } catch (_) { /* ok */ }
        }
      });
    }

    function duplicateTile(tileId) {
      const t = state.tiles.find(tt => tt.id === tileId);
      if (!t) return;
      const d = state.designs.find(dd => dd.id === t.designId);
      if (!d) return;
      const total = state.designs.reduce((s, dd) => s + dd.qty, 0);
      if (total >= cfg.maxDesigns) {
        showUploadError(`You've reached the ${cfg.maxDesigns}-design max for one sheet.`);
        return;
      }
      d.qty += 1;
      paint();
    }

    function deleteTile(tileId) {
      const t = state.tiles.find(tt => tt.id === tileId);
      if (!t) return;
      const d = state.designs.find(dd => dd.id === t.designId);
      if (!d) return;
      if (d.qty > 1) {
        d.qty -= 1;
      } else {
        state.designs = state.designs.filter(dd => dd.id !== d.id);
      }
      state.selectedTileIds.delete(tileId);
      paint();
    }

    function rotateTile(tileId) {
      // Single-tile rotate rotates the whole design (all copies).
      const t = state.tiles.find(tt => tt.id === tileId);
      if (!t) return;
      rotateDesign(t.designId);
    }

    /* ============================================================
       INTERACTIONS — drag/resize per tile, snap, selection
       ============================================================ */

    function attachTilePointerEvents(tileEl, tile) {
      // Click on a handle = resize. Click on the tile body = drag.
      // Click on an action button = its own listener (handled inline).
      tileEl.addEventListener('pointerdown', (ev) => {
        // Skip when target is an action button (keep their own click events).
        const actionBtn = ev.target.closest('[data-tile-action]');
        if (actionBtn) return;

        // Skip when right-click / non-primary
        if (ev.button !== 0) return;

        const handleEl = ev.target.closest('.tl-gsb__handle');
        if (handleEl) {
          startResize(ev, tile, handleEl.dataset.handle);
        } else {
          startDrag(ev, tile, ev.shiftKey);
        }
      });

      // Action buttons
      tileEl.querySelectorAll('[data-tile-action]').forEach(btn => {
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const a = btn.dataset.tileAction;
          if (a === 'rotate') rotateTile(tile.id);
          else if (a === 'duplicate') duplicateTile(tile.id);
          else if (a === 'delete') deleteTile(tile.id);
        });
      });
    }

    function selectTile(tileId, additive) {
      if (!additive) state.selectedTileIds.clear();
      if (tileId) state.selectedTileIds.add(tileId);
      paintSheetAndTiles();
    }

    function startDrag(ev, tile, additive) {
      const tileEl = ev.currentTarget;
      tileEl.setPointerCapture && tileEl.setPointerCapture(ev.pointerId);
      tileEl.classList.add('is-dragging');

      // Adjust selection
      if (!state.selectedTileIds.has(tile.id) || additive) {
        selectTile(tile.id, additive);
      }

      const sheetRect = dom.sheet.getBoundingClientRect();
      const startPxPerIn = state.pxPerIn;
      const grabXPx = ev.clientX - sheetRect.left;
      const grabYPx = ev.clientY - sheetRect.top;

      const movingIds = new Set(state.selectedTileIds);
      // Snapshot start positions of all moving tiles (multi-drag).
      const starts = {};
      movingIds.forEach(id => {
        const tt = state.tiles.find(t => t.id === id);
        if (tt) starts[id] = { x: tt.xIn, y: tt.yIn };
      });
      const tileStart = starts[tile.id] || { x: tile.xIn, y: tile.yIn };

      let dragged = false;

      function onMove(e) {
        e.preventDefault();
        // Compute the cumulative drag delta from the original grab point.
        // Using start position (captured once) keeps multi-tile drags
        // consistent across frames; using current xIn would compound.
        const px = startPxPerIn;
        const dxIn = (e.clientX - sheetRect.left - grabXPx) / px;
        const dyIn = (e.clientY - sheetRect.top - grabYPx) / px;
        if (Math.abs(dxIn) > 0.005 || Math.abs(dyIn) > 0.005) dragged = true;

        // Compute snap on the *primary* tile, then apply same delta to all selected.
        const proposedX = clamp(tileStart.x + dxIn, 0, cfg.sheetWidthIn - tile.wIn);
        const proposedY = Math.max(0, tileStart.y + dyIn);
        const snap = computeSnap(tile, proposedX, proposedY, movingIds);
        const finalX = clamp(snap.x, 0, cfg.sheetWidthIn - tile.wIn);
        const finalY = Math.max(0, snap.y);
        const adjDx = finalX - tileStart.x;
        const adjDy = finalY - tileStart.y;

        movingIds.forEach(id => {
          const tt = state.tiles.find(t => t.id === id);
          if (!tt) return;
          const start = starts[id];
          if (!start) return;
          tt.xIn = clamp(start.x + adjDx, 0, cfg.sheetWidthIn - tt.wIn);
          tt.yIn = Math.max(0, start.y + adjDy);
        });

        renderGuides(snap.guides);
        paintSheetAndTilesLite(movingIds);
      }

      function onUp(e) {
        tileEl.releasePointerCapture && tileEl.releasePointerCapture(ev.pointerId);
        tileEl.classList.remove('is-dragging');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        clearGuides();
        if (dragged) {
          // Manual placement may bump packedHeight; recompute without re-pack.
          recomputePackedHeight();
          paintLengthChips();
          paintFillMeter();
          paintSummary();
        }
      }

      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    }

    function startResize(ev, tile, corner) {
      const tileEl = ev.currentTarget;
      tileEl.setPointerCapture && tileEl.setPointerCapture(ev.pointerId);

      selectTile(tile.id, false);

      const startX = ev.clientX;
      const startY = ev.clientY;
      const startW = tile.wIn;
      const startH = tile.hIn;
      const startTileX = tile.xIn;
      const startTileY = tile.yIn;
      const ratio = startW / startH;
      const px = state.pxPerIn;

      function onMove(e) {
        e.preventDefault();
        const dx = (e.clientX - startX) / px;
        const dy = (e.clientY - startY) / px;
        let newW, newH, newX, newY;
        // Always lock aspect ratio. Use the larger of |dx|, |dy| projected on ratio.
        let driverW;
        if (corner.includes('e')) {
          driverW = startW + dx;
        } else {
          driverW = startW - dx;
        }
        let driverH;
        if (corner.includes('s')) {
          driverH = startH + dy;
        } else {
          driverH = startH - dy;
        }
        // Pick whichever pulls bigger so the cursor "leads" naturally.
        const fromW = driverW;
        const fromH = driverH * ratio;
        if (Math.abs(fromW - startW) >= Math.abs(fromH - startW)) {
          newW = fromW;
        } else {
          newW = fromH;
        }
        newW = clamp(newW, MIN_TILE_IN, cfg.sheetWidthIn);
        newH = newW / ratio;

        // Anchor based on which corner was grabbed.
        if (corner.includes('e')) newX = startTileX;
        else newX = startTileX + (startW - newW);
        if (corner.includes('s')) newY = startTileY;
        else newY = startTileY + (startH - newH);

        // Clamp to sheet bounds
        if (newX < 0) { newX = 0; newW = clamp(startTileX + startW, MIN_TILE_IN, cfg.sheetWidthIn); newH = newW / ratio; }
        if (newX + newW > cfg.sheetWidthIn) { newW = cfg.sheetWidthIn - newX; newH = newW / ratio; }
        if (newY < 0) { newH = startTileY + startH; newW = newH * ratio; newY = 0; }

        tile.xIn = newX;
        tile.yIn = newY;
        tile.wIn = newW;
        tile.hIn = newH;

        // Resize affects the underlying design (so all copies of that design follow).
        const d = state.designs.find(dd => dd.id === tile.designId);
        if (d) {
          if (d.rotation === 90) {
            // tile is in rotated orientation (h, w swapped vs design intrinsic)
            d.wIn = newH;
            d.hIn = newW;
          } else {
            d.wIn = newW;
            d.hIn = newH;
          }
        }

        paintSheetAndTilesLite(new Set([tile.id]));
      }

      function onUp() {
        tileEl.releasePointerCapture && tileEl.releasePointerCapture(ev.pointerId);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onUp);
        // Mirror new dims to all copies of this design and re-pack.
        paint();
      }

      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    }

    function paintSheetAndTilesLite(movingIds) {
      // Update only the positions/sizes of the moving tiles instead of full re-render.
      const px = state.pxPerIn;
      movingIds.forEach(id => {
        const t = state.tiles.find(tt => tt.id === id);
        if (!t) return;
        const el = dom.sheet.querySelector('[data-tile-id="' + id + '"]');
        if (!el) return;
        el.style.left   = (t.xIn * px) + 'px';
        el.style.top    = (t.yIn * px) + 'px';
        el.style.width  = (t.wIn * px) + 'px';
        el.style.height = (t.hIn * px) + 'px';
        const sz = el.querySelector('.tl-gsb__tile-size');
        if (sz) sz.textContent = `${t.wIn.toFixed(2)}″ × ${t.hIn.toFixed(2)}″`;
      });
    }

    function recomputePackedHeight() {
      let max = 0;
      state.tiles.forEach(t => { max = Math.max(max, t.yIn + t.hIn); });
      const prevLength = state.sheetLengthIn;
      state.packedHeightIn = max;
      // If user dragged a tile past the current sheet length, grow the sheet visually.
      // The variant chip will mark the next-bigger length as recommended automatically.
      if (state.packedHeightIn > state.sheetLengthIn - 0.5) {
        applyVariantSelection();
      }
      if (state.sheetLengthIn !== prevLength) {
        updateScale();
        paintRulers();
        // Resize the sheet element so the dragged tile isn't hanging in air.
        const px = state.pxPerIn;
        dom.sheet.style.width  = Math.round(cfg.sheetWidthIn * px) + 'px';
        dom.sheet.style.height = Math.round(state.sheetLengthIn * px) + 'px';
      }
    }

    /* ---------- Snap engine ---------- */

    function computeSnap(activeTile, proposedX, proposedY, excludeIds) {
      const px = state.pxPerIn;
      const thresholdIn = SNAP_THRESHOLD_PX / px;
      const w = activeTile.wIn;
      const h = activeTile.hIn;
      const guides = [];
      const skip = excludeIds || new Set([activeTile.id]);

      // Snap targets: sheet edges, other tiles' edges, quarter-inch grid.
      const targetsX = [0, cfg.sheetWidthIn]; // left, right edge
      const targetsY = [0]; // top edge
      state.tiles.forEach(t => {
        if (skip.has(t.id)) return;
        targetsX.push(t.xIn, t.xIn + t.wIn);
        targetsY.push(t.yIn, t.yIn + t.hIn);
      });
      // Grid
      for (let g = 0; g <= cfg.sheetWidthIn; g += GRID_SNAP_IN) targetsX.push(g);
      // (We don't pre-add Y grid because Y is unbounded; we'll snap dynamically below.)

      // Try to align the active tile's left, right and center to any target X.
      let snapX = proposedX, snapXDelta = Infinity;
      const aLefts = [proposedX, proposedX + w, proposedX + w / 2];
      const aLeftLabels = ['L', 'R', 'C'];
      aLefts.forEach((aL, i) => {
        targetsX.forEach(t => {
          if (Math.abs(aL - t) < thresholdIn && Math.abs(aL - t) < snapXDelta) {
            snapXDelta = Math.abs(aL - t);
            // Solve for new tile X: aL aligned to t means tileX shifts by (t - aL).
            snapX = proposedX + (t - aL);
            guides.push({ axis: 'v', xIn: t });
          }
        });
      });

      // Y snapping
      let snapY = proposedY, snapYDelta = Infinity;
      const aTops = [proposedY, proposedY + h, proposedY + h / 2];
      // Add Y grid lines centered around current proposedY (within threshold).
      const yGridStart = Math.floor((proposedY - 1) / GRID_SNAP_IN) * GRID_SNAP_IN;
      const yGridEnd   = Math.ceil((proposedY + h + 1) / GRID_SNAP_IN) * GRID_SNAP_IN;
      const dynTargetsY = targetsY.slice();
      for (let g = yGridStart; g <= yGridEnd; g += GRID_SNAP_IN) dynTargetsY.push(g);

      aTops.forEach(aT => {
        dynTargetsY.forEach(t => {
          if (Math.abs(aT - t) < thresholdIn && Math.abs(aT - t) < snapYDelta) {
            snapYDelta = Math.abs(aT - t);
            snapY = proposedY + (t - aT);
            guides.push({ axis: 'h', yIn: t });
          }
        });
      });

      // Filter guides: keep only those that survived as the chosen snap.
      const survived = [];
      if (snapXDelta !== Infinity) {
        guides.filter(g => g.axis === 'v').forEach(g => {
          // Check if this guide is on the chosen X line (snapX shifted to t).
          // Acceptable if any of L/R/C of the snapped position equals t.
          const lefts = [snapX, snapX + w, snapX + w / 2];
          if (lefts.some(L => approxEq(L, g.xIn, 0.005))) survived.push(g);
        });
      }
      if (snapYDelta !== Infinity) {
        guides.filter(g => g.axis === 'h').forEach(g => {
          const tops = [snapY, snapY + h, snapY + h / 2];
          if (tops.some(T => approxEq(T, g.yIn, 0.005))) survived.push(g);
        });
      }

      return { x: snapX, y: snapY, guides: survived };
    }

    function renderGuides(guides) {
      clearGuides();
      const px = state.pxPerIn;
      const seen = new Set();
      guides.forEach(g => {
        const key = g.axis + ':' + (g.axis === 'v' ? g.xIn : g.yIn).toFixed(3);
        if (seen.has(key)) return;
        seen.add(key);
        const el = document.createElement('div');
        el.className = 'tl-gsb__guide tl-gsb__guide--' + g.axis;
        if (g.axis === 'v') el.style.left = (g.xIn * px) + 'px';
        else el.style.top = (g.yIn * px) + 'px';
        el.dataset.guide = '1';
        dom.sheet.appendChild(el);
      });
    }

    function clearGuides() {
      dom.sheet.querySelectorAll('[data-guide]').forEach(el => el.remove());
    }

    /* ============================================================
       KEYBOARD SHORTCUTS
       ============================================================ */

    function attachKeyboard() {
      // Listen on document but only act when the sheet (or one of its
      // descendants) has focus, so we don't hijack page-wide typing.
      document.addEventListener('keydown', (e) => {
        if (!root.contains(document.activeElement)) return;
        // Don't run when a form input is focused (avoid stealing typing).
        const tag = (document.activeElement.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (state.selectedTileIds.size === 0) return;

        const ids = Array.from(state.selectedTileIds);

        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          ids.forEach(id => deleteTile(id));
          return;
        }
        if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
          e.preventDefault();
          ids.forEach(id => duplicateTile(id));
          return;
        }
        // Arrows nudge by 0.0625″ (1/16″), or 0.5″ with shift.
        const step = e.shiftKey ? 0.5 : 0.0625;
        let dx = 0, dy = 0;
        if (e.key === 'ArrowLeft')  dx = -step;
        if (e.key === 'ArrowRight') dx = step;
        if (e.key === 'ArrowUp')    dy = -step;
        if (e.key === 'ArrowDown')  dy = step;
        if (dx === 0 && dy === 0) return;
        e.preventDefault();
        ids.forEach(id => {
          const t = state.tiles.find(tt => tt.id === id);
          if (!t) return;
          t.xIn = clamp(t.xIn + dx, 0, cfg.sheetWidthIn - t.wIn);
          t.yIn = Math.max(0, t.yIn + dy);
        });
        recomputePackedHeight();
        paintSheetAndTilesLite(state.selectedTileIds);
        paintLengthChips();
        paintFillMeter();
        paintSummary();
      });
    }

    /* ============================================================
       PNG COMPOSITE EXPORT (download + base64 preview)
       ============================================================ */

    function renderComposite(pxPerIn, opts) {
      opts = opts || {};
      const w = Math.max(64, Math.round(cfg.sheetWidthIn * pxPerIn));
      const h = Math.max(64, Math.round(state.sheetLengthIn * pxPerIn));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Background
      ctx.fillStyle = opts.transparent ? 'rgba(0,0,0,0)' : '#fafafa';
      ctx.fillRect(0, 0, w, h);

      // Optional border + label for the download version.
      if (!opts.minimal) {
        ctx.strokeStyle = 'rgba(0, 212, 255, 0.6)';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, w - 2, h - 2);
      }

      // Tiles
      state.tiles.forEach(t => {
        const d = state.designs.find(dd => dd.id === t.designId);
        if (!d || !d.img) return;
        const x = Math.round(t.xIn * pxPerIn);
        const y = Math.round(t.yIn * pxPerIn);
        const tw = Math.round(t.wIn * pxPerIn);
        const th = Math.round(t.hIn * pxPerIn);
        ctx.save();
        if (d.rotation === 90) {
          // Rotate around tile center
          ctx.translate(x + tw / 2, y + th / 2);
          ctx.rotate(Math.PI / 2);
          // After rotation, draw with swapped dims back to natural orientation.
          ctx.drawImage(d.img, -th / 2, -tw / 2, th, tw);
        } else {
          ctx.drawImage(d.img, x, y, tw, th);
        }
        ctx.restore();
      });

      return c;
    }

    function downloadPng() {
      if (state.isDownloading || !state.tiles.length) return;
      state.isDownloading = true;
      try {
        const c = renderComposite(EXPORT_PX_PER_IN, { transparent: false });
        // Add a small footer with sheet info
        const ctx = c.getContext('2d');
        ctx.fillStyle = 'rgba(0, 212, 255, 0.9)';
        ctx.font = '600 ' + Math.max(10, Math.round(EXPORT_PX_PER_IN * 0.4)) + 'px -apple-system, "Inter", sans-serif';
        const v = variants.find(vv => vv.id === state.variantId);
        const lengthLabel = v ? (v.option1 || v.title) : inchesPretty(state.sheetLengthIn);
        ctx.fillText(`${cfg.sheetWidthIn}″ × ${lengthLabel} · ${state.designs.length} designs`, 12, 22);
        const dataUrl = c.toDataURL('image/png');
        const fname = (cfg.productHandle || 'gang-sheet') + '-' + Date.now() + '.png';
        downloadDataUrl(dataUrl, fname);
      } catch (e) {
        console.error('[tl-gsb] download failed', e);
      } finally {
        state.isDownloading = false;
      }
    }

    // Render the composite as a Blob (PNG). Used for the cart submit so the
    // sheet preview becomes a real downloadable file on the order in Shopify
    // admin (instead of a noisy base64 string).
    function compositeBlob() {
      return new Promise((resolve) => {
        try {
          const c = renderComposite(EXPORT_PX_PER_IN, { transparent: false });
          // Add a small footer with sheet info — same as the customer download.
          const ctx = c.getContext('2d');
          const v = variants.find(vv => vv.id === state.variantId);
          const lengthLabel = v ? (v.option1 || v.title) : inchesPretty(state.sheetLengthIn);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.fillRect(0, c.height - 24, c.width, 24);
          ctx.fillStyle = 'rgba(0, 212, 255, 0.95)';
          ctx.font = '600 14px -apple-system, "Inter", sans-serif';
          ctx.fillText(`${cfg.sheetWidthIn}″ × ${lengthLabel} · ${state.designs.length} designs · spacing ${state.spacingIn}″`, 10, c.height - 7);
          c.toBlob((blob) => resolve(blob), 'image/png');
        } catch (e) {
          console.error('[tl-gsb] composite render failed', e);
          resolve(null);
        }
      });
    }

    /* ============================================================
       SUBMIT — POST /cart/add.js (multipart) with variant + designs
       ────────────────────────────────────────────────────────────
       Each uploaded design is attached as its OWN line-item-property
       FILE so that the order admin in Shopify shows a download link
       per design. Shopify auto-stores files on its CDN and renders
       them as clickable download links right next to the line item.

       Property names visible to the customer (no leading underscore):
         "Sheet preview"      → composite PNG (so they can confirm)
         "Design 1 — N copies" → original artwork file (× designs.length)
         "Sheet width"        → "16″"
         "Sheet length"       → "5 Feet"
         "Total designs"      → "8"
         "Total copies"       → "23"

       Hidden from customer (admin-only, leading underscore):
         "_Layout"            → JSON describing positions/rotations
         "_Spacing"           → 0.25 in
         "_Fill efficiency"   → 82%
       ============================================================ */

    // Pretty quantity helper
    function qtyLabel(n) { return n === 1 ? '1 copy' : n + ' copies'; }

    function setSubmitProgress(pct, msg) {
      dom.atc.style.setProperty('--tl-gsb-progress', Math.round(pct) + '%');
      if (msg && dom.atcLabel) dom.atcLabel.textContent = msg;
    }

    async function submit() {
      if (state.isSubmitting) return;
      const v = variants.find(vv => vv.id === state.variantId);
      if (!v) { showError('Select a sheet length first.'); return; }
      if (!state.tiles.length) { showError('Add at least one design before adding to cart.'); return; }
      if (state.packedHeightIn > v.lengthIn + 0.05) {
        showError('Your designs are taller than this sheet length. Pick a longer sheet or remove some designs.');
        return;
      }

      // Pre-flight: total upload cap. Shopify caps multipart cart-add bodies
      // at roughly 100 MB on most plans — bigger than that gets rejected.
      const totalBytes = state.designs.reduce((s, d) => s + (d.file ? d.file.size : 0), 0);
      const HARD_CAP_BYTES = 100 * 1024 * 1024;
      if (totalBytes > HARD_CAP_BYTES) {
        const mb = (totalBytes / 1024 / 1024).toFixed(0);
        showError(`Your designs total ${mb} MB — Shopify's per-order upload limit is around 100 MB. Please remove a few or use lower-resolution copies.`);
        return;
      }

      state.isSubmitting = true;
      dom.atc.classList.add('is-loading', 'is-uploading');
      dom.atc.disabled = true;
      const oldLabel = dom.atcLabel.textContent;
      setSubmitProgress(0, 'Rendering preview…');

      let previewBlob = null;
      try {
        previewBlob = await compositeBlob();
      } catch (_) { /* preview is nice-to-have, not blocking */ }

      try {
        // Layout JSON for production team to re-render at full resolution.
        const layout = state.tiles.map(t => {
          const d = state.designs.find(dd => dd.id === t.designId);
          return {
            design: d ? d.name : '',
            x_in: +t.xIn.toFixed(3),
            y_in: +t.yIn.toFixed(3),
            w_in: +t.wIn.toFixed(3),
            h_in: +t.hIn.toFixed(3),
            rotation: d ? d.rotation : 0,
          };
        });
        const designsList = state.designs.map(d => ({
          name: d.name,
          natural_w: d.naturalW,
          natural_h: d.naturalH,
          w_in: +d.wIn.toFixed(3),
          h_in: +d.hIn.toFixed(3),
          qty: d.qty,
          rotation: d.rotation,
        }));

        const totalSqIn = state.tiles.reduce((s, t) => s + t.wIn * t.hIn, 0);
        const sheetSqIn = cfg.sheetWidthIn * v.lengthIn;
        const fillPct = sheetSqIn > 0 ? Math.round((totalSqIn / sheetSqIn) * 100) : 0;
        const totalCopies = state.designs.reduce((s, d) => s + d.qty, 0);

        const fd = new FormData();
        fd.append('id', String(v.id));
        fd.append('quantity', '1');

        // Composite preview — visible to BOTH customer (in their order email
        // and account) and admin (download link in the order detail page).
        if (previewBlob) {
          const previewName = (cfg.productHandle || 'gang-sheet') + '-preview.png';
          fd.append('properties[Sheet preview]', previewBlob, previewName);
        }

        // Each design becomes its own download-able file property.
        // Including the qty in the property NAME (not the value, which has to
        // be the file) means the admin sees "Design 3 — 5 copies: logo.png"
        // at a glance, no need to cross-reference a separate quantity field.
        state.designs.forEach((d, i) => {
          if (!d.file) return;
          const propName = `Design ${i + 1} — ${qtyLabel(d.qty)}`;
          fd.append('properties[' + propName + ']', d.file, d.name);
        });

        // Customer-visible metadata
        fd.append('properties[Sheet width]', cfg.sheetWidthIn + '″');
        fd.append('properties[Sheet length]', v.option1 || v.title);
        fd.append('properties[Total designs]', String(state.designs.length));
        fd.append('properties[Total copies]', String(totalCopies));

        // Admin-only (leading-underscore properties are hidden from customer
        // emails / order pages but still visible to merchants in the admin)
        fd.append('properties[_Spacing]', state.spacingIn + '″');
        fd.append('properties[_Fill efficiency]', fillPct + '%');
        fd.append('properties[_Layout]', JSON.stringify({
          sheet_w_in: cfg.sheetWidthIn,
          sheet_h_in: v.lengthIn,
          spacing_in: state.spacingIn,
          tiles: layout,
          designs: designsList,
        }));

        // Tolerate themes where routes.cart_add_url already ends in `.js`.
        const cartAddUrl = cfg.cartAddUrl.endsWith('.js')
          ? cfg.cartAddUrl
          : cfg.cartAddUrl + '.js';

        // Use XHR so we can show real upload progress (fetch() doesn't expose
        // upload progress in any browser as of writing).
        setSubmitProgress(0, 'Uploading designs…');
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', cartAddUrl, true);
          xhr.setRequestHeader('Accept', 'application/json');

          xhr.upload.addEventListener('progress', (ev) => {
            if (!ev.lengthComputable) return;
            const pct = (ev.loaded / ev.total) * 100;
            setSubmitProgress(pct, `Uploading… ${Math.round(pct)}%`);
          });

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 400) {
              setSubmitProgress(100, 'Added to cart!');
              try { resolve(JSON.parse(xhr.responseText)); }
              catch (_) { resolve({}); }
            } else if (xhr.status === 413) {
              reject(new Error('Your designs are too large for one upload. Please remove a few or replace any over 20 MB with smaller copies.'));
            } else {
              let msg = 'Cart add failed: HTTP ' + xhr.status;
              try {
                const body = JSON.parse(xhr.responseText);
                msg = body.description || body.message || msg;
              } catch (_) { /* keep default */ }
              reject(new Error(msg));
            }
          };
          xhr.onerror = () => reject(new Error('Network error while uploading designs.'));
          xhr.ontimeout = () => reject(new Error('Upload timed out — try fewer or smaller files.'));
          xhr.timeout = 5 * 60 * 1000; // 5 minutes
          xhr.send(fd);
        });

        // Fetch the full cart so we can dispatch a properly-shaped
        // CartUpdateEvent (the theme's cart-icon, cart-drawer, etc.
        // listen on document for `cart:update` and read event.detail.resource
        // — see assets/events.js).
        let cart = null;
        try {
          const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (cartRes.ok) cart = await cartRes.json();
        } catch (_) { /* non-fatal */ }

        const updateEvt = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: root.id,
            data: {
              source: 'tl-gang-sheet-builder',
              variantId: v.id,
              productId: cart && result && result.product_id ? String(result.product_id) : undefined,
              itemCount: cart ? cart.item_count : undefined,
            },
          },
        });
        document.dispatchEvent(updateEvt);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        root.dispatchEvent(new CustomEvent('tl:gsb:added-to-cart', {
          bubbles: true,
          detail: { variantId: v.id, totalDesigns: state.designs.length, totalCopies }
        }));

        // Try to open the cart drawer; if no drawer is mounted, redirect.
        const drawer = document.querySelector('cart-drawer-component, [data-cart-drawer], cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
          // Reset the builder so a customer can build another sheet without
          // the previous designs lingering. Free their object URLs too.
          setTimeout(() => {
            revokeAllDesignUrls();
            state.designs = [];
            state.tiles = [];
            state.selectedTileIds.clear();
            state.variantOverridden = false;
            dom.atc.classList.remove('is-loading', 'is-uploading');
            dom.atc.style.removeProperty('--tl-gsb-progress');
            dom.atc.disabled = false;
            dom.atcLabel.textContent = oldLabel;
            state.isSubmitting = false;
            paint();
          }, 600);
        } else {
          window.location.href = cfg.cartUrl;
        }
      } catch (e) {
        console.error('[tl-gsb] submit failed', e);
        showError(e.message || 'Could not add this gang sheet to your cart. Please try again.');
        dom.atc.classList.remove('is-loading', 'is-uploading');
        dom.atc.style.removeProperty('--tl-gsb-progress');
        dom.atcLabel.textContent = oldLabel;
        dom.atc.disabled = false;
        state.isSubmitting = false;
      }
    }

    function showError(msg) {
      if (!dom.err) return;
      dom.err.textContent = msg;
      dom.err.hidden = false;
      // Auto-hide after a few seconds.
      clearTimeout(showError._t);
      showError._t = setTimeout(() => { dom.err.hidden = true; }, 8000);
    }

    /* ============================================================
       EVENT WIRING
       ============================================================ */

    function bindEvents() {
      // File input
      dom.fileInput.addEventListener('change', () => {
        addFiles(dom.fileInput.files);
        dom.fileInput.value = '';
      });

      // Drop zone (sidebar upload label)
      dom.drop.addEventListener('dragover', (e) => { e.preventDefault(); dom.drop.classList.add('is-hover'); });
      dom.drop.addEventListener('dragleave', () => dom.drop.classList.remove('is-hover'));
      dom.drop.addEventListener('drop', (e) => {
        e.preventDefault();
        dom.drop.classList.remove('is-hover');
        if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
      });

      // Whole-canvas drop zone
      dom.frame.addEventListener('dragover', (e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
        e.preventDefault();
        dom.frame.classList.add('is-dropping');
      });
      dom.frame.addEventListener('dragleave', (e) => {
        // Only clear when leaving the frame, not when entering a child.
        if (e.relatedTarget && dom.frame.contains(e.relatedTarget)) return;
        dom.frame.classList.remove('is-dropping');
      });
      dom.frame.addEventListener('drop', (e) => {
        if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
        e.preventDefault();
        dom.frame.classList.remove('is-dropping');
        addFiles(e.dataTransfer.files);
      });

      // Spacing pill row — initialize active state from merchant default,
      // then wire click handlers.
      const spacingButtons = Array.from(dom.spacingGroup.querySelectorAll('[data-tl-gsb-spacing-val]'));
      spacingButtons.forEach(btn => {
        const isDefault = approxEq(parseFloat(btn.dataset.tlGsbSpacingVal), state.spacingIn, 0.001);
        btn.classList.toggle('is-active', isDefault);
        btn.addEventListener('click', () => {
          spacingButtons.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          state.spacingIn = parseFloat(btn.dataset.tlGsbSpacingVal) || 0.25;
          paint();
        });
      });

      // Auto-arrange
      dom.autoArrange.addEventListener('click', () => {
        // Wipe manual positions for a true auto-arrange: we do this by
        // rebuilding tiles from scratch rather than from current positions.
        state.tiles = [];
        state.selectedTileIds.clear();
        paint();
      });

      // Download
      dom.download.addEventListener('click', downloadPng);

      // ATC
      dom.atc.addEventListener('click', submit);

      // Click on empty sheet area = deselect
      dom.sheet.addEventListener('pointerdown', (e) => {
        if (e.target === dom.sheet) {
          state.selectedTileIds.clear();
          paintSheetAndTiles();
        }
      });

      // Re-scale on resize
      let rzTimer;
      window.addEventListener('resize', () => {
        clearTimeout(rzTimer);
        rzTimer = setTimeout(() => {
          updateScale();
          paintRulers();
          paintSheetAndTiles();
        }, 80);
      });
    }

    bindEvents();
    attachKeyboard();
    paint();
  }

  /* ============================================================
     BOOTSTRAP
     ============================================================ */

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-tl-gsb]').forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor / dynamic section reloads
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:select', (e) => initAll(e.target));
})();
